<script setup>
import { computed, h, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import FilterSelect from './inputs/FilterSelect.vue';
import ConditionRow from './ConditionRow.vue';

defineProps({
  groupIndex: { type: Number, required: true },
  totalGroups: { type: Number, required: true },
  filterTypes: { type: Array, required: true },
});

const emit = defineEmits(['removeGroup']);
const { t } = useI18n();

const conditions = defineModel('conditions', {
  type: Array,
  default: () => [],
});
const groupOperator = defineModel('groupOperator', {
  type: String,
  default: 'AND',
});

const DEFAULT_CONDITION = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const addCondition = () => {
  conditions.value.push({ ...DEFAULT_CONDITION });
};

const removeCondition = index => {
  if (conditions.value.length > 1) {
    conditions.value.splice(index, 1);
  }
};

const groupOperatorOptions = computed(() => [
  {
    label: t('FILTER.QUERY_DROPDOWN_LABELS.AND'),
    value: 'AND',
    icon: h('span', { class: 'i-lucide-ampersands !text-n-blue-text' }),
  },
  {
    label: t('FILTER.QUERY_DROPDOWN_LABELS.OR'),
    value: 'OR',
    icon: h('span', { class: 'i-woot-logic-or !text-n-blue-text' }),
  },
]);

const conditionRowRefs = useTemplateRef('conditionRowRefs');

const validate = () => {
  if (!conditionRowRefs.value) return true;
  return conditionRowRefs.value.every(row => row && row.validate());
};

defineExpose({ validate });
</script>

<template>
  <div class="border border-n-weak rounded-xl p-4 grid gap-3 bg-n-alpha-1">
    <div class="flex items-center justify-between">
      <span
        class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
      >
        {{ $t('CONTACTS_LAYOUT.FILTER.GROUP.LABEL', { n: groupIndex + 1 }) }}
      </span>
      <Button
        v-if="totalGroups > 1"
        xs
        ghost
        ruby
        icon="i-lucide-trash-2"
        @click="emit('removeGroup')"
      />
    </div>

    <ul class="grid gap-3 list-none">
      <ConditionRow
        v-for="(condition, idx) in conditions"
        ref="conditionRowRefs"
        :key="idx"
        v-model:attribute-key="condition.attributeKey"
        v-model:filter-operator="condition.filterOperator"
        v-model:values="condition.values"
        v-model:query-operator="condition.queryOperator"
        :show-query-operator="idx > 0"
        :filter-types="filterTypes"
        @remove="removeCondition(idx)"
      />
    </ul>

    <Button xs ghost blue class="self-start" @click="addCondition">
      {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_CONDITION') }}
    </Button>
  </div>

  <div
    v-if="groupIndex < totalGroups - 1"
    class="flex items-center justify-center py-1"
  >
    <FilterSelect
      v-model="groupOperator"
      variant="faded"
      hide-icon
      class="text-sm font-semibold"
      :options="groupOperatorOptions"
    />
  </div>
</template>
